/**
 * AtomicElementRenderer
 *
 * Renders a single atomic element inside the editor canvas using local React
 * components. No middleware calls at render time — CSS variables and element
 * data are already stored in the box state by elementMapper at creation time.
 *
 * Architecture
 * ────────────
 * 1. Decode box.state.__text  →  { elementData, cssVariables }
 * 2. Wrap the subtree in <ThemeBridge cssVariables={…}>
 *    → injects CSS custom properties as inline style, resolving all var(--…)
 * 3. Dispatch to the matching type-renderer via RENDERERS map
 *
 * Adding a new element type
 * ─────────────────────────
 * 1. Create  renderers/MyTypeRenderer.jsx
 * 2. Import it here and add one line to the RENDERERS map below.
 */

import React, { useState, useRef, useEffect } from "react";
import i18n from "i18next";
import { ThemeBridge } from "./ThemeBridge";
import { resolveI18nDeep, decodeAtomicText } from "../atomic_box_plugin_config";
import { useEditorLayerTime } from "../contexts/editorLayerTimes";
import { AtomicAnimationTimeContext } from "../animations/AnimatedField";

// ─── Type renderers ────────────────────────────────────────────────────────────
import { TextRenderer } from "./renderers/TextRenderer";
import { BulletListRenderer } from "./renderers/BulletListRenderer";
import { CardGridRenderer } from "./renderers/CardGridRenderer";
import { TimelineRenderer } from "./renderers/TimelineRenderer";
import { QuoteRenderer } from "./renderers/QuoteRenderer";
import { StatGroupRenderer } from "./renderers/StatGroupRenderer";
import { TwoColumnRenderer } from "./renderers/TwoColumnRenderer";
import { ComparisonRenderer } from "./renderers/ComparisonRenderer";
import { HeroRenderer } from "./renderers/HeroRenderer";
import { SectionRenderer } from "./renderers/SectionRenderer";
import { DividerRenderer } from "./renderers/DividerRenderer";
import { BadgeRenderer } from "./renderers/BadgeRenderer";
import { CtaButtonRenderer } from "./renderers/CtaButtonRenderer";
import { IconBlockRenderer } from "./renderers/IconBlockRenderer";

const RENDERERS = {
  // Text primitives
  eyebrow: TextRenderer,
  heading: TextRenderer,
  footer: TextRenderer,

  // List
  "bullet-list": BulletListRenderer,
  bullet: BulletListRenderer,

  // Composite surfaces
  "card-grid": CardGridRenderer,
  timeline: TimelineRenderer,
  quote: QuoteRenderer,
  "two-column": TwoColumnRenderer,
  comparison: ComparisonRenderer,
  hero: HeroRenderer,
  section: SectionRenderer,
  "stat-group": StatGroupRenderer,
  stats: StatGroupRenderer,

  // Decorative / interactive
  divider: DividerRenderer,
  badge: BadgeRenderer,
  "cta-button": CtaButtonRenderer,
  "icon-block": IconBlockRenderer,
};

/**
 * Immutably set text at a dot-path inside obj.
 * Handles: plain string leaves, TextInput ({ text, animation }) objects,
 * and array indexing. Safe for plain-string array elements.
 *
 * Examples:
 *   setNestedText(el, "text", "new")          plain field
 *   setNestedText(el, "steps.1.body", "new")  TextInput inside array
 *   setNestedText(el, "items.0.text", "new")  plain-string array element
 */
function setNestedText(obj, path, newText) {
  const keys = path.split(".");
  if (keys.length === 1) {
    const k = keys[0];
    const cur = obj[k];
    const newVal =
      cur && typeof cur === "object" && "text" in cur
        ? { ...cur, text: newText } // preserve animation metadata
        : newText;
    return { ...obj, [k]: newVal };
  }
  const [head, ...rest] = keys;
  const child = obj[head];
  if (Array.isArray(child)) {
    const idx = parseInt(rest[0], 10);
    if (isNaN(idx)) {
      console.error(`[setNestedText] expected numeric index at "${rest[0]}" in path "${path}"`);
      return obj;
    }
    const remaining = rest.slice(1).join(".");
    const newArr = [...child];
    // Guard: plain-string array element with path pointing to .text
    if (typeof child[idx] === "string" && (remaining === "text" || remaining === "")) {
      newArr[idx] = newText;
    } else {
      newArr[idx] = setNestedText(child[idx], remaining, newText);
    }
    return { ...obj, [head]: newArr };
  }
  // When child is missing and the next segment is numeric, bootstrap an array
  if (child === undefined || child === null) {
    const idx = parseInt(rest[0], 10);
    if (!isNaN(idx)) {
      const remaining = rest.slice(1).join(".");
      const newArr = [];
      newArr[idx] = remaining ? setNestedText({}, remaining, newText) : newText;
      return { ...obj, [head]: newArr };
    }
  }
  return { ...obj, [head]: setNestedText(child ?? {}, rest.join("."), newText) };
}

/**
 * Immutably replace an array at a dot-path inside obj.
 * Unlike setNestedText this does NO TextInput merging — the array is
 * placed verbatim, preserving existing item types (string or object).
 */
function setNestedArray(obj, path, newArray) {
  const keys = path.split(".");
  if (keys.length === 1) {
    return { ...obj, [keys[0]]: newArray };
  }
  const [head, ...rest] = keys;
  const child = obj[head];
  if (Array.isArray(child)) {
    const idx = parseInt(rest[0], 10);
    if (!isNaN(idx)) {
      const remaining = rest.slice(1).join(".");
      const newArr = [...child];
      newArr[idx] = remaining ? setNestedArray(child[idx], remaining, newArray) : newArray;
      return { ...obj, [head]: newArr };
    }
  }
  return { ...obj, [head]: setNestedArray(child ?? {}, rest.join("."), newArray) };
}

// ─── Fallback ──────────────────────────────────────────────────────────────────

function UnknownElement({ type }) {
  return (
    <div
      style={{
        padding: "10px 14px",
        border: "1px dashed rgba(255,255,255,0.18)",
        borderRadius: "6px",
        color: "rgba(255,255,255,0.4)",
        fontSize: "11px",
        fontFamily: "monospace",
        userSelect: "none",
      }}
    >
      [{type || "element"} — unknown type]
    </div>
  );
}

// ─── Main ──────────────────────────────────────────────────────────────────────

/**
 * @param {object}   props.state    - Box plugin state ({ __text: URI-encoded JSON })
 * @param {string}   props.themeId  - Optional override
 * @param {string}   props.paletteId - Optional override
 * @param {function} props.onUpdate - props.update from EditorBox; saves to Redux
 */
function AtomicElementRenderer({
  state,
  boxId,
  onUpdate,
  visorPlaying,
  visorCurrentTime,
  animationTimeOriginSec,
  animationSceneId,
  themeCssVariables,
  fontScale,
}) {
  const [editing, setEditing] = useState(false);
  const [hovered, setHovered] = useState(false);
  // Force re-render when the UI language changes so i18n sentinels re-resolve.
  const [, setLang] = useState(i18n.language);
  const rootRef = useRef(null);

  // Editor timeline scrub: EditorApp sets this box's time while the playhead is over it.
  // Visor pipeline supplies visorCurrentTime as a prop instead.
  const editorLayerTime = useEditorLayerTime(boxId);
  const editorCurrentTimeSec = typeof editorLayerTime === "number" ? editorLayerTime : null;

  useEffect(() => {
    const handler = (lng) => setLang(lng);
    i18n.on("languageChanged", handler);
    return () => i18n.off("languageChanged", handler);
  }, []);

  // ── Decode stored state (before hooks that depend on decoded values) ───
  const decoded = decodeAtomicText(state?.__text || "{}") || {};

  // Support two storage formats:
  //   Wrapped  (elementMapper): { elementData: {...}, cssVariables: {...} }
  //   Raw      (AI agent/sample): { type: '...', ... } stored directly
  const elementData = decoded.elementData ?? (decoded.type ? decoded : null);
  const cssVariables = decoded.cssVariables ?? {};

  // ── Hooks — must be called unconditionally before any early return ─────

  useEffect(() => {
    if (!editing) return;
    function onDocPointerDown(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        const active = document.activeElement;
        if (active && rootRef.current.contains(active)) {
          active.blur();
        }
        setEditing(false);
      }
    }
    document.addEventListener("pointerdown", onDocPointerDown, true);
    return () => document.removeEventListener("pointerdown", onDocPointerDown, true);
  }, [editing]);

  // Called by child renderers when any text field changes.
  function handleChange(path, newText) {
    if (!elementData) return;
    const updated = setNestedText(elementData, path, newText);
    const newDecoded = decoded.elementData ? { ...decoded, elementData: updated } : updated;
    if (onUpdate) {
      onUpdate("__text", encodeURIComponent(JSON.stringify(newDecoded)));
    }
    setTimeout(() => {
      if (!rootRef.current?.contains(document.activeElement)) {
        setEditing(false);
      }
    }, 0);
  }

  // Called by child renderers to reorder an array field (e.g. items, steps, stats)
  function handleReorder(arrayPath, newArray) {
    if (!elementData) return;
    const updated = setNestedArray(elementData, arrayPath, newArray);
    const newDecoded = decoded.elementData ? { ...decoded, elementData: updated } : updated;
    if (onUpdate) onUpdate("__text", encodeURIComponent(JSON.stringify(newDecoded)));
  }

  // ── Derive animation state ────────────────────────────────────────────────
  // visorPlaying is:
  //   undefined → editor mode (animation only flows from Context scrub)
  //   true      → visor, currently playing
  //   false     → visor, paused
  const inVisor = visorPlaying !== undefined;
  // `playing` enables animation flow in renderers. In editor mode it stays
  // true whenever the Context is providing a currentTime (scrub/play tick);
  // when no scrub is happening, child fields render plain (no wrapper).
  const playing = inVisor || editorCurrentTimeSec !== null ? true : undefined;
  const isPaused = inVisor && visorPlaying === false;
  // Visor prop wins; otherwise fall back to editor scrub context.
  const currentTimeSec =
    typeof visorCurrentTime === "number" ? visorCurrentTime : editorCurrentTimeSec;

  // ── Early returns (after all hooks) ────────────────────────────────────
  if (!elementData) return null;

  const elementType = elementData.type || "unknown";
  const Renderer = RENDERERS[elementType];

  const displayElement = resolveI18nDeep(elementData);

  return (
    <AtomicAnimationTimeContext.Provider
      value={{ timeOriginSec: animationTimeOriginSec, sceneId: animationSceneId }}
    >
      <ThemeBridge
        cssVariables={cssVariables}
        themeCssVariables={themeCssVariables}
        fontScale={fontScale}
      >
        <div
          ref={rootRef}
          onDoubleClick={
            !inVisor
              ? (e) => {
                  e.stopPropagation();
                  setEditing(true);
                }
              : undefined
          }
          onMouseEnter={!inVisor ? () => setHovered(true) : undefined}
          onMouseLeave={!inVisor ? () => setHovered(false) : undefined}
          style={{
            width: "100%",
            height: "100%",
            position: "relative",
            overflow: "hidden",
            outline: !inVisor && editing ? "2px solid var(--color-text-accent, #6c63ff)" : "none",
            outlineOffset: "-2px",
          }}
        >
          {Renderer ? (
            <Renderer
              element={displayElement}
              editing={editing}
              onChange={handleChange}
              onReorder={(arrayPath, newArray) => handleReorder(arrayPath, newArray)}
              playing={playing}
              isPaused={isPaused}
              currentTimeSec={currentTimeSec}
            />
          ) : (
            <UnknownElement type={elementType} />
          )}
          {hovered && !editing && !inVisor && elementType !== "divider" && (
            <div
              style={{
                position: "absolute",
                bottom: "6px",
                right: "8px",
                fontSize: "10px",
                fontFamily: "monospace",
                color: "rgba(255,255,255,0.5)",
                pointerEvents: "none",
                userSelect: "none",
                background: "rgba(0,0,0,0.45)",
                padding: "2px 6px",
                borderRadius: "4px",
              }}
            >
              {i18n.t("messages.double_click_to_edit", { defaultValue: "double-click to edit" })}
            </div>
          )}
        </div>
      </ThemeBridge>
    </AtomicAnimationTimeContext.Provider>
  );
}

export default AtomicElementRenderer;
